import { ReactNode } from "react";
import { Card } from "~/components/ui/card";
import { cn } from "~/lib/utils";

export interface OverviewTile {
  label: string;
  content: ReactNode;
}

/**
 * The shared metadata "stat row" — a single bordered card split into evenly
 * spaced columns, each a small uppercase label over a larger value. Reused
 * across the project/dataset overviews and the agent profile so the three
 * "homepages" share one visual grammar.
 */
export function OverviewTiles({ tiles, className }: { tiles: OverviewTile[]; className?: string }) {
  return (
    <Card
      className={cn(
        "flex flex-row flex-wrap divide-x divide-border overflow-hidden p-0",
        className
      )}
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="flex min-w-36 flex-1 flex-col items-center gap-1.5 px-5 py-4 text-center"
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {tile.label}
          </span>
          <div className="text-base text-foreground">{tile.content}</div>
        </div>
      ))}
    </Card>
  );
}

interface EntityOverviewHeaderProps {
  // Leading lucide icon shown in a bordered circle next to the name.
  icon?: ReactNode;
  title: ReactNode;
  // Metadata tiles, rendered left-to-right in a single bordered row. Pages
  // share the same leading tiles (My Role, Owners, Last Updated) then append
  // entity-specific ones so the overviews feel like one family.
  tiles: OverviewTile[];
  // The editable description block (each page wires its own editor).
  children: ReactNode;
  className?: string;
}

/**
 * Shared "homepage" header for projects and datasets, mirroring the agent
 * profile layout: an icon + name header, then the metadata tile row, then the
 * editable description. Presentational only — each page supplies its own data
 * and description editor.
 */
export function EntityOverviewHeader({
  icon,
  title,
  tiles,
  children,
  className,
}: EntityOverviewHeaderProps) {
  return (
    <div className={cn("flex flex-col gap-5", className)}>
      <div className="flex items-center gap-3">
        {icon && (
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full border border-border text-foreground">
            {icon}
          </div>
        )}
        {title}
      </div>

      <OverviewTiles tiles={tiles} />

      <div>{children}</div>
    </div>
  );
}
